import { useState } from "react";
import { Plus, RotateCcw, Upload, Crown, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useRefreshLimit } from "@/hooks/useRefreshLimit";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { pickResumeFromDrive } from "@/lib/googleDrivePicker";
import { GoogleDriveIcon } from "@/components/GoogleDriveIcon";

interface ChatActionsProps {
  onRefreshChat: () => void;
  onUploadResume?: (file: File) => void | Promise<boolean | void>;
  userProfile?: { subscription_tier?: string } | null;
  isConversationComplete: boolean;
  onAddLoadingMessage?: (message: string) => string | null;
  onRemoveLoadingMessage?: (messageId: string) => void;
  /** Open the inline Drive smart-search card (preferred over native picker). */
  onOpenDriveSearch?: () => void;
}

export function ChatActions({ onRefreshChat, onUploadResume, userProfile, isConversationComplete, onAddLoadingMessage, onRemoveLoadingMessage, onOpenDriveSearch }: ChatActionsProps) {
  const T = useTranslation().t('chatActions', { returnObjects: true })

  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { canRefresh, remainingRefreshes, incrementRefreshCount } = useRefreshLimit();
  const { user } = useAuth();

  const handleRefreshChat = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: T["authRequired"],
        variant: "destructive",
      });
      return;
    }

    if (!canRefresh) {
      toast({
        title: "Refresh Limit Reached",
        description: T["refreshLimitReached"],
        variant: "destructive",
      });
      return;
    }

    if (!isConversationComplete) {
      toast({
        title: "Conversation in progress",
        description: "You can refresh the chat once the current conversation is complete.",
      });
      return;
    }

    const success = await incrementRefreshCount();
    if (success) {
      onRefreshChat();
      toast({
        title: "Chat Refreshed",
        description: `${T["chatRefresh1"]} ${remainingRefreshes - 1} ${T["chatRefresh2"]}`,
      });
    }
    setIsOpen(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadResume) return;

    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: T["invalidFileType"],
        variant: "destructive",
      });
      return;
    }

    let loadingMessageId: string | null = null;
    try {
      setIsUploading(true);
      
      // Add immediate loading message with dots
      if (onAddLoadingMessage) {
        loadingMessageId = onAddLoadingMessage("Uploading your resume");
      }
      
      const uploadAccepted = await onUploadResume(file);
      if (uploadAccepted === false) return;
      toast({
        title: "Upload complete",
        description: `${file.name} ${T["uploadedComplete"]}`,
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: T["uploadFailed"],
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setIsOpen(false);
      e.target.value = "";
      
      // Remove loading message after a short delay to allow chat loading to take over
      if (loadingMessageId && onRemoveLoadingMessage) {
        setTimeout(() => onRemoveLoadingMessage(loadingMessageId), 500);
      }
    }
  };

  const handleGoogleDrivePick = async () => {
    if (!onUploadResume) return;
    setIsOpen(false);

    // Preferred path: open the inline smart-search card so the user doesn't
    // have to deal with the heavy native Picker UI.
    if (onOpenDriveSearch) {
      onOpenDriveSearch();
      return;
    }

    let loadingMessageId: string | null = null;
    try {
      setIsUploading(true);

      // Add immediate loading message with dots
      if (onAddLoadingMessage) {
        loadingMessageId = onAddLoadingMessage("Uploading your resume");
      }

      const file = await pickResumeFromDrive();
      const uploadAccepted = await onUploadResume(file);
      if (uploadAccepted === false) return;
      toast({
        title: "Upload complete",
        description: `${file.name} ${T["uploadedComplete"]}`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message === "Picker cancelled") return;
      toast({
        title: "Google Drive upload failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      
      // Remove loading message after a short delay to allow chat loading to take over
      if (loadingMessageId && onRemoveLoadingMessage) {
        setTimeout(() => onRemoveLoadingMessage(loadingMessageId), 500);
      }
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full border-input-border hover:border-primary"
          aria-label="Open actions menu"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={handleRefreshChat} disabled={!canRefresh || !isConversationComplete}>
          <RotateCcw className="h-4 w-4 mr-2" />
          <span>{T["refreshChat"]}</span>
          {!canRefresh && (
            <Crown className="h-3 w-3 ml-auto text-yellow-500" />
          )}
        </DropdownMenuItem>

        {onUploadResume && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Upload className="h-4 w-4 mr-2" />
              <span>{isUploading ? T["uploading"] : T["uploadResume"]}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              <DropdownMenuItem onClick={handleGoogleDrivePick}>
                <GoogleDriveIcon className="h-4 w-4 mr-2 shrink-0" />
                <span>Google Drive</span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <label htmlFor="resume-upload-input" className="cursor-pointer flex items-center gap-0 w-full">
                  <Paperclip className="h-4 w-4 mr-2" />
                  <span>From Device</span>
                  <input
                    id="resume-upload-input"
                    type="file"
                    accept=".pdf,.docx"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                </label>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
